import React, { useCallback, useEffect, useMemo } from 'react';
import { View } from 'react-native';
import Animated, { useAnimatedScrollHandler } from 'react-native-reanimated';
import { AppView, ButtonGroup, Spacer, rem } from 'design-system';
import Button, { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import StatusBar from 'design-system/StatusBar';
import FlatListItemCard from 'design-system/FlatListItemCard';
import SectionHeader from 'design-system/SectionHeader';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import RowAccount from 'features/accounts/components/RowAccount';
import { getVRPConsents } from 'features/pre-approved-transfers/actions';
import { selectHasPreApprovedTransfersFeature, selectVRPConsentsArray, } from 'features/pre-approved-transfers/selectors';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import { selectGBAccountsWithPISPInfo, selectValidGBAccounts } from 'reducers/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import createStyleSheets from 'utils/createStyleSheets';
import ListEmptyView from 'design-system/ListEmptyView';
import { isWeb } from '../../../constants';
const selectorParams = {
    filterBusiness: true,
};
const SelectAccountToTopup = withAnimatedNavigationHeader(({ scrollY, route, navigation }) => {
    const flow = route.params?.flow;
    const styles = useStyles(styleSet);
    const dispatch = useAppDispatch();
    useEffect(() => {
        if (flow !== 'preApprovedTransferSettings') {
            dispatch(getVRPConsents());
        }
    }, [flow]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const accounts = useAppSelector((store) => selectValidGBAccounts(store, selectorParams));
    const { notSupported: unsupportedAccounts } = useAppSelector(selectGBAccountsWithPISPInfo);
    const preApprovedAccounts = useAppSelector((store) => selectVRPConsentsArray(store));
    const accountsWithPreApprovedFiltered = useMemo(() => accounts.filter((acc) => preApprovedAccounts.find((a) => a.account.id === acc.id) === undefined), [accounts, preApprovedAccounts]);
    const hasPreApprovedTransfersFeature = useAppSelector(selectHasPreApprovedTransfersFeature);
    const unsupportedItems = useMemo(() => unsupportedAccounts.map((account) => ({ type: 'unsupported', account })), [unsupportedAccounts]);
    const data = useMemo(() => {
        if (!hasPreApprovedTransfersFeature) {
            return [
                ...accounts.map((account) => ({
                    type: 'account',
                    account,
                })),
                ...unsupportedItems,
            ];
        }
        const accountsMapped = accountsWithPreApprovedFiltered.map((account) => ({
            type: 'account',
            account,
        }));
        if (flow !== 'preApprovedTransferSettings' && preApprovedAccounts.length) {
            return [
                {
                    type: 'header',
                    title: 'Pre-approved transfers',
                },
                ...preApprovedAccounts.map((account) => ({ type: 'pre-approved', account })),
                ...accountsMapped,
                ...unsupportedItems,
            ];
        }
        if (flow === 'preApprovedTransferSettings') {
            return accountsMapped.filter((acc) => acc.account.supportsVrp);
        }
        return [...accountsMapped, ...unsupportedItems];
    }, [
        accounts,
        accountsWithPreApprovedFiltered,
        flow,
        hasPreApprovedTransfersFeature,
        preApprovedAccounts,
        unsupportedItems,
    ]);
    const hasAnyAccounts = accounts.length > 0 || unsupportedAccounts.length > 0;
    const handleConnectAccount = useCallback(() => {
        navigation.navigateDeprecated('ChooseBank', { back: true, investments: false });
    }, []);
    const scrollHandler = useAnimatedScrollHandler((handle) => {
        // eslint-disable-next-line no-param-reassign
        scrollY.value = handle.contentOffset.y;
    });
    const handlePress = useCallback((row) => () => {
        if (row.type === 'pre-approved') {
            if (flow === 'preApprovedTransferSettings') {
                // Should not happen
                return;
            }
            navigation.navigateDeprecated('InvestTopUp', {
                from: row.account,
                flow: flow,
            });
        }
        else if (row.type === 'account') {
            if (flow === 'preApprovedTransferSettings') {
                navigation.navigateDeprecated('PreApprovedTransferSetLimits', { bankAccount: row.account });
            }
            else {
                navigation.navigateDeprecated('InvestTopUp', {
                    from: row.account,
                    flow,
                });
            }
        }
    }, [flow]);
    const renderItem = useCallback(({ item, index }) => {
        if (item.type === 'header') {
            return (<>
              <Spacer h={8}/>
              <SectionHeader horizontalMargins toolTipTitle="What are pre-approved transfers?" toolTipSubTitle="Pre-approved transfers allow you to deposit in Emma from your bank without leaving the app. You can edit them in Settings.">
                {item.title}
              </SectionHeader>
              <Spacer h={16}/>
            </>);
        }
        let indexInSection = index;
        let { length } = data;
        if (hasPreApprovedTransfersFeature) {
            length =
                item.type === 'pre-approved'
                    ? preApprovedAccounts.length
                    : accountsWithPreApprovedFiltered.length + unsupportedAccounts.length;
            if (flow !== 'preApprovedTransferSettings' && preApprovedAccounts.length) {
                indexInSection = item.type === 'pre-approved' ? index - 1 : index - preApprovedAccounts.length - 1;
            }
            else if (flow === 'preApprovedTransferSettings') {
                length = data.length;
            }
        }
        const isUnsupported = item.type === 'unsupported';
        return (<>
            {hasPreApprovedTransfersFeature &&
                preApprovedAccounts.length > 0 &&
                index === preApprovedAccounts.length + 1 ? (<Spacer h={16}/>) : null}
            <FlatListItemCard index={indexInSection} itemsLength={length} style={styles.card} onPress={isUnsupported ? undefined : handlePress(item)}>
              <View pointerEvents="box-only" style={isUnsupported ? styles.disabledContent : undefined}>
                <RowAccount primaryBalanceOnly hideSpaceUserAvatar disabled={isUnsupported} style={styles.rowAccount} hideActionRequiredText={isUnsupported} notSupportedText={isUnsupported ? 'Not supported' : undefined} data={item.type === 'pre-approved' ? item.account.account : item.account}/>
              </View>
            </FlatListItemCard>
          </>);
    }, [
        accountsWithPreApprovedFiltered.length,
        data,
        flow,
        handlePress,
        hasPreApprovedTransfersFeature,
        preApprovedAccounts.length,
        unsupportedAccounts.length,
        styles.card,
        styles.disabledContent,
        styles.rowAccount,
    ]);
    const contentContainerStyles = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, styles.contentContainerStyles, paddingHorizontalStyle);
    return (<AppView withFrame={false}>
        <StatusBar barStyle="dark-content" animated/>
        <Animated.FlatList data={data} onScroll={scrollHandler} scrollEventThrottle={16} renderItem={renderItem} contentContainerStyle={data ? contentContainerStyles : {}} ListEmptyComponent={<ListEmptyView onCard title="There are no accounts"/>}/>
        <ButtonGroup type="floating">
          <Button {...(accounts.length > 0 ? { brandReversed: true } : { brand: true })} title={hasAnyAccounts ? 'Connect another account' : 'Connect an account'} onPress={handleConnectAccount}/>
        </ButtonGroup>
      </AppView>);
}, ({ colors }) => ({
    title: 'Select an account',
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styleSet = createStyleSheets((colors) => ({
    rowAccount: {
        paddingHorizontal: rem(16),
    },
    contentContainerStyles: {
        paddingTop: rem(16),
    },
    card: {
        backgroundColor: colors.cards.onDark,
    },
    disabledContent: {
        opacity: 0.5,
    },
}));
export default SelectAccountToTopup;
